import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useRef } from "react";
import {
  DLQInfoResult,
  DLQRedriveInfoResult,
} from "../lusca-search-api/generated-src";
import { DlqRedriveInfoApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

export type DlqRedriveStatus = "unknown" | "loading" | "success" | "error";

export interface DlqRedriveResult {
  status: DlqRedriveStatus;
  error?: string;
}

export interface DlqRedriveInfo {
  redrivenMessages?: Map<string, DlqRedriveResult>;
  error: Error | null;
}

export default function useFetchDlqRedriveInfo(
  stage: string,
  messageId: string,
  enable: boolean
): DlqRedriveInfo {
  const DlqRedriveApi = DlqRedriveInfoApiFactory();
  const queryClient = useQueryClient();
  const hasTriggeredImmediateFetch = useRef(false);

  const queryKey = ["dlqRedriveInfo", stage, [messageId]];
  const { data, error } = useQuery<DLQRedriveInfoResult, Error>(
    queryKey,
    async () => {
      const currentData =
        queryClient.getQueryData<DLQRedriveInfoResult>(queryKey);
      const executionArn = currentData?.executionArn;

      const { data }: AxiosResponse<DLQRedriveInfoResult> =
        await DlqRedriveApi.getDLQRedriveInfo(stage, [messageId], executionArn);

      return data;
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enable,
      refetchInterval: (data) => {
        if (data?.status === "PENDING") {
          if (data.executionArn && !hasTriggeredImmediateFetch.current) {
            // Trigger immediate fetch only for the first time
            hasTriggeredImmediateFetch.current = true;

            setTimeout(() => {
              queryClient.invalidateQueries(queryKey).catch((error) => {
                console.error("Error invalidating queries:", error);
              });
            }, 0);
          }
          return 1000; // Continue polling every second while pending
        }
        return false; // Stop refetching once we're not in PENDING state
      },

      onSuccess: (data) => {
        if (data.status === "PENDING" && data.executionArn) {
          queryClient.setQueryData<DLQRedriveInfoResult>(
            queryKey,
            (oldData) => ({
              ...oldData,
              ...data,
            })
          );
        }
      },
      onError: (err) => {
        const axiosError = err as { response?: { data?: { message?: string } } };
        const message = axiosError.response?.data?.message || err.message;
        queryClient.setQueryData<DLQRedriveInfoResult>(
          queryKey,
          () => ({ status: "ERROR", error: message } as DLQRedriveInfoResult)
        );
      },
    }
  );

  const status: (string) => DlqRedriveResult = (
    key: string
  ): DlqRedriveResult => {
    if (error) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message = axiosError.response?.data?.message || error.message;
      return { status: "error", error: message };
    }
    if (!data) return { status: "loading" };
    switch (data.status) {
      case "PENDING":
        return { status: "loading" };
      case "COMPLETED":
        const itemStatus = data?.result?.redrivenMessages?.[key];
        if (itemStatus === "SUCCESS") {
          return { status: "success" };
        }
        return { status: "error", error: `Item error: ${itemStatus}` };
      case "ERROR":
        return { status: "error", error: data.error || "Unknown error" };
      default:
        return { status: "error", error: data.error || "Unknown error" };
    }
  };
  return {
    redrivenMessages: new Map([messageId].map((key) => [key, status(key)])),
    error: error || null,
  };
}
